import axios from "axios";
import dayjs from "dayjs";
import jwt_decode from "jwt-decode";
import { useContext } from "react";
import BASE_URL from "../constants";
import AuthContext from "../context/AuthContext";

const baseURL = `${BASE_URL}`;

// 同時に複数の axios インスタンスから refresh 要求が発生しても、実際のリフレッシュ処理は
// 1 回だけ実行するためのモジュールスコープの共有 Promise（シングルフライト化）。
// 本番設定は ROTATE_REFRESH_TOKENS=True のため refresh token は使用するたび無効化される。
// 並行リクエストがそれぞれ個別に refresh を呼ぶと、後発のリクエストは必ず 401 になり
// 正当なユーザーが誤ってログアウトされてしまう。
//
// user_id ごとに Promise を分けている理由: 同じタブ内で別ユーザーへ切り替わった直後など、
// 前ユーザーの refresh が進行中のタイミングで次ユーザーの期限切れリクエストが来た場合、
// 単一の共有 Promise だと後続リクエストも前ユーザーの更新結果（アクセストークン）を
// そのまま Authorization に設定してしまい、別ユーザー権限で API を呼んでしまう。
const refreshPromisesByUser = new Map();

const requestRefresh = (authTokens, setAuthTokens, setUser, currentUserId) => {
	if (!refreshPromisesByUser.has(currentUserId)) {
		// axios インスタンス生成時のクロージャに閉じ込められた authTokens ではなく、
		// 一次ソースである localStorage から最新の refresh トークンを読み直す。
		// ROTATE_REFRESH_TOKENS=True 環境では refresh するたびに旧トークンが無効化されるため、
		// 別インスタンス（古いクロージャ）が先に refresh 済みの場合、古い refresh トークンを
		// そのまま送ると 401 になり正当なユーザーが誤ってログアウトされてしまう。
		const storedTokensRaw = localStorage.getItem("authTokens");
		const storedTokens = storedTokensRaw ? JSON.parse(storedTokensRaw) : null;

		// localStorage は同一ブラウザの別タブで別ユーザーが再ログインすると
		// そのユーザーの authTokens に置き換わる。currentUserId（このクロージャの
		// ユーザー）と一致する場合のみ「同一ユーザーのより新しいトークン」として
		// 採用し、一致しない・確認できない場合はクロージャ自身の authTokens を使う。
		// ここを無条件に localStorage 優先にすると、別ユーザーの refresh token を
		// 使ってこのタブが別人のセッションに切り替わってしまう
		// （他人のデータを誤って操作・閲覧しうる）。
		let refreshToken = authTokens?.refresh;
		if (storedTokens?.refresh) {
			try {
				const storedUserId = jwt_decode(storedTokens.access).user_id;
				if (storedUserId !== undefined && storedUserId === currentUserId) {
					refreshToken = storedTokens.refresh;
				}
			} catch {
				// デコード失敗時はクロージャ側の値を使う（安全側にフォールバック）
			}
		}

		const promise = axios
			.post(`${baseURL}/api/token/refresh/`, {
				refresh: refreshToken,
			})
			.then((response) => {
				// Issue #184: 応答が返るまでの間に別タブで新しいログインやトークン更新が
				// 起きていると、保存値は要求を出した時点のものと別物になっている。そこへ
				// 無条件に書き込むと新しいセッションを古い世代へ巻き戻してしまい、共有端末では
				// 次の利用者の画面に前の利用者のデータが出る。要求開始時に読んだ生の保存値と
				// 突き合わせ、変わっていないときだけ従来どおり保存・同期する。
				if (localStorage.getItem("authTokens") === storedTokensRaw) {
					localStorage.setItem("authTokens", JSON.stringify(response.data));
					setAuthTokens(response.data);
					setUser(jwt_decode(response.data.access));
				}

				// 保存値が入れ替わっていても、この更新自体は成功しているので、取得した
				// トークンでこのリクエストだけは完了させる。画面側は storage イベントで
				// 新しい利用者へ切り替わり（この応答を受け取るコンポーネントは
				// 利用者 ID をキーに再マウントされる）、古い応答は捨てられる。
				return response.data;
			})
			.catch((error) => {
				// Issue #88: 失敗した refresh が「実際にどのトークンだったか」を
				// 呼び出し元へ伝える。logoutUser 側はこれと現在の保存値を突き合わせて、
				// 本当にセッションが切れたのか（＝送ったトークン自身が無効）、
				// 別タブが先に更新しただけなのか（＝保存値が別物に変わっている）を
				// 推測でなく事実で判定する。
				error.sentRefreshToken = refreshToken ?? null;
				throw error;
			})
			.finally(() => {
				refreshPromisesByUser.delete(currentUserId);
			});

		refreshPromisesByUser.set(currentUserId, promise);
	}
	return refreshPromisesByUser.get(currentUserId);
};

// refresh トークン自体が無効と確定できる場合のみ true を返す。
// ネットワークエラーやサーバー障害（応答なし・5xx 等）のような不確定な失敗では
// 既存セッションを破壊しない。
const isRefreshTokenInvalid = (error) => {
	const status = error?.response?.status;
	return status === 401 || status === 403;
};

const useAxios = () => {
	const { authTokens, setUser, setAuthTokens, logoutUser } =
		useContext(AuthContext);

	const axiosInstance = axios.create({
		baseURL,
		headers: { Authorization: `Bearer ${authTokens?.access}` },
	});

	axiosInstance.interceptors.request.use(async (req) => {
		const user = jwt_decode(authTokens.access);
		const isExpired = dayjs.unix(user.exp).diff(dayjs()) < 1;
		if (!isExpired) return req;

		try {
			const data = await requestRefresh(
				authTokens,
				setAuthTokens,
				setUser,
				user.user_id,
			);
			req.headers.Authorization = `Bearer ${data.access}`;
			return req;
		} catch (error) {
			// refresh トークン自体が失効・無効と確定した場合のみログイン画面へ遷移させる。
			// （そのまま req を返すと期限切れの access token でリクエストが送信され続ける）
			// ネットワークエラー等の不確定な失敗で logoutUser() を呼ぶと、ROTATE_REFRESH_TOKENS
			// 環境下の並行リクエストで正当なユーザーまで誤ってログアウトされてしまうため呼ばない。
			if (isRefreshTokenInvalid(error)) {
				logoutUser({
					sessionExpired: true,
					failedRefreshToken: error?.sentRefreshToken ?? null,
				});
			}
			return Promise.reject(error);
		}
	});

	return axiosInstance;
};

export default useAxios;
